/* Autorouting's main-thread half — M17.6/M10.4.
 *
 * The search itself lives in $map/router.worker.ts; this is the promise
 * wrapper (same request/id/pending-map shape terrain-protocol.ts already
 * uses for the merged-DEM worker) plus the reactive state a settings sheet
 * and the route editor read directly.
 *
 * ═══ ADVISORY. SAID OUT LOUD, NOT JUST IN A COMMENT. ═══
 *
 * The CHS NONNA licence (plan §5.3) requires this notice on anything built
 * from the data, and M17.10 asks for advisory framing throughout — so it is
 * carried as data here (`CHS_NOTICE`) precisely so the UI has no excuse to
 * quietly drop it. A router that produces a beautiful route is MORE
 * dangerous than one that produces an ugly one, because it invites trust;
 * this is the surface that keeps that trust honest.
 */

import type {
  MinDepthReply,
  MinDepthRequest,
  RouteReply,
  RouteRequest
} from '@gennetay/chart-core/map/router.worker';
/* The package spawns its own worker — see map/workers.ts. The URL in a
 * `new Worker(new URL(…))` is resolved against the file it is written in, so it
 * cannot live out here. */
import { spawnRouterWorker } from '@gennetay/chart-core/map/workers';
import type { DepthSetting, LatLon, RouteWarning } from '@gennetay/chart-core/lib/router';
import type { TimedRequest } from '@gennetay/chart-core/map/router.worker';
import type { CurrentGeom, PackedHour } from '@gennetay/chart-core/lib/seacurrents';
import {
  scheduleFor,
  type CurrentSampler,
  type Gate,
  type PassageModel,
  type Schedule
} from '@gennetay/chart-core/lib/passage';
import { PLANNED_SPEED_KN } from '@gennetay/chart-core/lib/routesummary';

export const CHS_NOTICE =
  'This route was made by Gennetay Dash and contains intellectual property of the ' +
  'Canadian Hydrographic Service (CHS) of the Department of Fisheries and Oceans. It ' +
  'does not meet the requirements of the Navigation Safety Regulations, 2020 under the ' +
  'Canada Shipping Act, 2001. Charts and publications issued by or on the authority of ' +
  'CHS must be used to meet the requirements of those regulations. ADVISORY ONLY — a ' +
  'planning aid, never a navigation system. Verify every leg against a real chart before ' +
  'relying on it.';

const SETTING_KEY = 'gd.autoroute.setting';
const CURRENTS_KEY = 'gd.autoroute.currents';
const SPEED_KEY = 'gd.autoroute.speedKn';

/** Knots. The planning default the route summary already uses, so a boat that
 *  has never touched the setting plans at the same speed both panels assume. */
export const DEFAULT_BOAT_KN = PLANNED_SPEED_KN;
/** A sanity range, not a claim about any boat: below this a "passage" is drift,
 *  and above it the shipped tidal-stream stations stop gating anything. */
export const MIN_BOAT_KN = 1;
export const MAX_BOAT_KN = 30;

export const SETTING_LABEL: Record<DepthSetting, string> = {
  nearest: 'NEAREST',
  near: 'NEAR',
  normal: 'NORMAL',
  far: 'FAR'
};

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<
  number,
  { ok: (r: RouteReply | MinDepthReply) => void; fail: (e: Error) => void }
>();

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = spawnRouterWorker();
  worker.onmessage = (ev: MessageEvent<RouteReply | MinDepthReply>) => {
    const p = pending.get(ev.data.id);
    if (!p) return;
    pending.delete(ev.data.id);
    p.ok(ev.data);
  };
  worker.onerror = (ev: ErrorEvent) => {
    // A worker-level failure (the module itself throwing on load, say) has
    // no `id` to route to a single caller — fail every request in flight
    // rather than leaving them hanging with a spinner that never resolves.
    for (const p of pending.values()) p.fail(new Error(ev.message || 'router worker failed'));
    pending.clear();
  };
  return worker;
}

async function requestRoute(req: Omit<RouteRequest, 'id' | 'kind'>): Promise<RouteReply> {
  const w = ensureWorker();
  const id = ++seq;
  return new Promise((ok, fail) => {
    pending.set(id, {
      ok: (r) => ok(r as RouteReply),
      fail
    });
    w.postMessage({ kind: 'route', id, ...req } satisfies RouteRequest);
  });
}

async function postMinDepth(pts: LatLon[]): Promise<MinDepthReply> {
  const w = ensureWorker();
  const id = ++seq;
  return new Promise((ok, fail) => {
    pending.set(id, {
      ok: (r) => ok(r as MinDepthReply),
      fail
    });
    w.postMessage({ kind: 'minDepth', id, pts } satisfies MinDepthRequest);
  });
}

class Autoroute {
  setting = $state<DepthSetting>(load());

  /* ═══ THE CURRENT-AWARE HALF ═══
   *
   * Off by default, and that is not timidity. A timed route needs a departure,
   * a speed and a current field to mean anything; with the box unticked the
   * router does exactly what it has always done, which is the behaviour every
   * existing proof measures. */
  useCurrents = $state<boolean>(loadBool(CURRENTS_KEY, false));
  boatKn = $state<number>(loadNum(SPEED_KEY, DEFAULT_BOAT_KN));
  /** Epoch ms. Null means "now", re-read at each solve rather than frozen at
   *  page load — a plan made at 09:00 and solved at 11:00 must not quietly be
   *  a plan for 09:00. */
  departT = $state<number | null>(null);
  /** The schedule for `result`, when the timed search answered. */
  schedule = $state<Schedule | null>(null);
  computing = $state(false);
  /** Set together, always — a result and an error are never both current. */
  result = $state<LatLon[] | null>(null);
  warnings = $state<RouteWarning[]>([]);
  /** Distinct from `error`: this is a real answer ("no path exists"), not a
   *  failure of the router itself. M17.7 — "grey inline", the caller's job;
   *  this just says why there is nothing to draw. */
  unsolvable = $state<string | null>(null);
  /** How far each endpoint had to move to reach navigable water.
   *
   *  The router snaps a pin onto water rather than refusing — most harbours are
   *  land cells at the cost grid's ~153 m. That is the right behaviour and it
   *  MUST be visible: a route that starts 500 m from where you pressed is fine
   *  if it admits it, and a lie if it does not. */
  snapped = $state<{ startM: number; endM: number } | null>(null);
  /** The path solved for a drag that is still in progress.
   *
   *  Kept SEPARATE from `result` and never written into the editor while the
   *  finger is down. `editor.recalc` replaces the whole point list, and the
   *  drag is holding an INDEX into that list — swapping it underneath would
   *  leave the gesture moving whichever point happened to land at that index,
   *  or none. So a live solve is drawn as a preview and only committed on
   *  release, where the index no longer matters. */
  preview = $state<LatLon[] | null>(null);
  /** True while a live solve is outstanding, so the strip can say so without
   *  the whole draft flickering between states. */
  previewing = $state(false);
  error = $state<string | null>(null);
  /** The live route summary's depth probe, M5.8: shallowest charted depth
   *  along the current draft, or null before the first answer. Independent of
   *  `route()` — it is asked on every draft edit, never blocks one, and a
   *  failed probe must not interrupt anything. */
  minDepth = $state<{ metres: number | null; unknown: boolean } | null>(null);
  #minDepthSeq = 0;

  setSetting(s: DepthSetting): void {
    this.setting = s;
    try {
      localStorage.setItem(SETTING_KEY, s);
    } catch {
      /* the choice still holds for this session */
    }
  }

  clear(): void {
    this.result = null;
    this.warnings = [];
    this.unsolvable = null;
    this.snapped = null;
    this.error = null;
    this.minDepth = null;
    this.clearPreview();
  }

  clearPreview(): void {
    this.preview = null;
    this.previewing = false;
    this.#want = null;
  }

  /* ═══ WHERE THE STORES ARE PLUGGED IN ═══
   *
   * chart-core is headless: the current field and the tidal-stream stations
   * live in chart-layers, and this module must not reach up into them. The host
   * app wires them once at boot, the same way `configureSources` does for
   * endpoints. Unset means the checkbox has nothing to work with, which is the
   * correct state for a host with no current data at all. */
  #sources: PassageSources | null = null;

  configurePassage(s: PassageSources | null): void {
    this.#sources = s;
  }

  /** True when the box can be ticked at all — there are gates or a field. */
  canUseCurrents(): boolean {
    return this.#sources !== null;
  }

  setUseCurrents(on: boolean): void {
    this.useCurrents = on;
    try {
      localStorage.setItem(CURRENTS_KEY, String(on));
    } catch {
      /* a setting that does not persist is still a setting */
    }
  }

  setBoatKn(kn: number): void {
    const v = Math.min(MAX_BOAT_KN, Math.max(MIN_BOAT_KN, kn));
    if (!Number.isFinite(v)) return;
    this.boatKn = v;
    try {
      localStorage.setItem(SPEED_KEY, String(v));
    } catch {
      /* as above */
    }
  }

  /** The departure actually planned against — `departT` when parked, otherwise
   *  now, read at solve time rather than at page load. */
  departAt(): number {
    return this.departT ?? Date.now();
  }

  /** The passage model, for deriving a schedule on this side. Null when the
   *  current-aware path is not in play. */
  model(): PassageModel | null {
    const s = this.#sources;
    if (!s || !this.useCurrents) return null;
    return {
      boatKn: this.boatKn,
      current: s.sampler(),
      gates: s.gates(this.boatKn)
    };
  }

  /** The worker's half of the same thing. Null when the box is off. */
  timed(): TimedRequest | null {
    const s = this.#sources;
    if (!s || !this.useCurrents) return null;
    const f = s.field();
    return {
      boatKn: this.boatKn,
      departT: this.departAt(),
      gates: s.gates(this.boatKn),
      ...(f ? { field: { geom: f.geom, hours: f.hours }, fieldKey: f.key } : {})
    };
  }

  /* ═══ ONE SOLVE IN FLIGHT, ALWAYS THE LATEST QUESTION ═══
   *
   * A drag produces pointer moves far faster than the router answers: solves
   * measure 6-44 ms on the shipped grid, and a finger emits a move every
   * ~10 ms. Posting one request per move would queue them in the worker — it
   * is single-threaded — so the preview would fall further behind the finger
   * the longer the drag went on, and every answer but the last would be thrown
   * away after being paid for.
   *
   * So: at most one request outstanding, and the most recent unanswered
   * question replaces any older one. That is a trailing-edge throttle whose
   * period is the solve time itself, which is exactly the right cadence — it
   * speeds up on short legs and backs off on long ones without a magic number.
   */
  #want: { via: LatLon[]; draftM: number } | null = null;
  #inFlight = false;

  /**
   * Solve for a drag in progress, THROUGH the given points.
   *
   * `via` is the route reduced to the points a person actually chose: the
   * start, whatever is being dragged, and the end. Everything between them was
   * the router's own work and is re-solved.
   *
   * Solving THROUGH rather than merely between matters. Dragging a middle
   * point and re-solving start→end would ignore the drag completely — the
   * answer does not pass through the point the finger is holding, so the route
   * would snap back and the gesture would look broken. Legs are solved
   * pairwise and joined, which is also cheaper: two short searches beat one
   * long one on this grid.
   *
   * Cheap to call on every pointer move.
   */
  routeLive(via: readonly LatLon[], draftM: number): void {
    if (via.length < 2) return;
    this.#want = { via: via.map((p) => $state.snapshot(p)), draftM };
    if (this.#inFlight) return;
    void this.#drain();
  }

  async #drain(): Promise<void> {
    this.#inFlight = true;
    this.previewing = true;
    try {
      while (this.#want) {
        const w = this.#want;
        this.#want = null;

        const path: LatLon[] = [];
        let snapped: { startM: number; endM: number } | null = null;
        let unsolvable: string | null = null;
        let superseded = false;

        for (let i = 1; i < w.via.length; i++) {
          let r;
          try {
            r = await requestRoute({
              start: w.via[i - 1]!,
              end: w.via[i]!,
              draftM: w.draftM,
              setting: this.setting
            });
          } catch {
            unsolvable = null;
            break; // a failed live solve is not worth reporting mid-drag
          }
          /* A newer question arrived while this leg was being answered — its
           * answer is about a point the finger has already left, and the legs
           * after it would be solved against stale ends. Abandon the whole
           * path rather than joining fresh legs to stale ones. */
          if (this.#want) {
            superseded = true;
            break;
          }
          if (r.unsolvable) {
            unsolvable = r.unsolvable;
            break;
          }
          const pts = r.pts ?? [];
          if (i === 1) {
            snapped = r.snapped ?? null;
            path.push(...pts);
          } else {
            /* Drop the joint: the previous leg already ended there. */
            path.push(...pts.slice(1));
            if (r.snapped && snapped) snapped = { startM: snapped.startM, endM: r.snapped.endM };
          }
        }

        if (superseded) continue;
        this.preview = unsolvable ? null : path.length > 1 ? path : null;
        this.snapped = snapped;
        this.unsolvable = unsolvable;
      }
    } finally {
      this.#inFlight = false;
      this.previewing = false;
    }
  }

  /** The endpoint nudge, as a sentence, or null when neither moved.
   *  Rounded to 10 m: the grid's own cell is 153 m, so a metre is invented
   *  precision that would read as a survey. */
  snapNote(): string | null {
    const s = this.snapped;
    if (!s) return null;
    const r = (m: number) => `${Math.round(m / 10) * 10} m`;
    if (s.startM < 10 && s.endM < 10) return null;
    if (s.startM >= 10 && s.endM >= 10) {
      return `Start moved ${r(s.startM)} and destination ${r(s.endM)} to reach charted water.`;
    }
    return s.startM >= 10
      ? `Start moved ${r(s.startM)} to reach charted water.`
      : `Destination moved ${r(s.endM)} to reach charted water.`;
  }

  /**
   * Compute a route between two points at the current setting. Draft comes
   * from the caller (the boat's configured draft, M17.9) rather than being
   * read from config in here — this class does not know about `$config`,
   * matching the rest of this app's habit of keeping stores focused on one
   * thing.
   */
  async route(start: LatLon, end: LatLon, draftM: number): Promise<void> {
    this.computing = true;
    this.clear();
    try {
      /* $state.snapshot, not the objects as handed in. `start`/`end` are
       * frequently a caller's own $state read straight through — a picked
       * point held in ChartPage's autorouteStart, the boat's live `fix` —
       * and a Svelte 5 $state object is a Proxy, which postMessage's
       * structured-clone algorithm refuses outright: "could not be cloned",
       * thrown at the postMessage call itself, before the worker sees
       * anything. Plain data only crosses a worker boundary. */
      const timed = this.timed();
      const r = await requestRoute({
        start: $state.snapshot(start),
        end: $state.snapshot(end),
        draftM,
        setting: this.setting,
        /* CALLED, not tested for existence. `this.timed` is a method and so is
         * always truthy — the first version spread `{ timed: null }` into every
         * request, which the worker reads as "current-aware, with nothing
         * configured". */
        ...(timed ? { timed } : {})
      });
      /* Set even on the unsolvable path: "the destination moved 600 m and
       * there is still no way through" is a more useful answer than either
       * half alone. */
      this.snapped = r.snapped ?? null;
      if (r.error) this.error = r.error;
      else if (r.unsolvable) this.unsolvable = r.unsolvable;
      else {
        this.result = r.pts ?? null;
        this.warnings = r.warnings ?? [];
        /* The schedule is derived on the SIMPLIFIED points by the same module
         * the search costed with, so the strip and the route cannot disagree
         * about when the boat is where. Left null when the plain search
         * answered — a schedule against a route that never considered the
         * water would be a number with no claim behind it. */
        const m = this.model();
        this.schedule =
          r.arriveT !== undefined && r.pts && m ? scheduleFor(r.pts, this.departAt(), m) : null;
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.computing = false;
    }
  }

  /**
   * Probe the cost grid for the shallowest charted depth along `pts` — the
   * summary strip's MIN DEPTH, M5.8. Fire-and-forget by design: a drag moves
   * the draft a hundred times a second and this must never block it, so the
   * latest request wins and earlier replies are discarded wholesale rather
   * than rendered over a newer draft's answer.
   */
  async requestMinDepth(pts: LatLon[]): Promise<void> {
    const seq = ++this.#minDepthSeq;
    try {
      const r = await postMinDepth(pts);
      if (seq === this.#minDepthSeq && !r.error)
        this.minDepth = { metres: r.metres, unknown: r.unknown };
    } catch {
      /* a failed probe is not worth interrupting anything */
    }
  }
}

/** What a caller supplies so this store can build a timed request. Injected
 *  rather than imported, because the current field and the tidal-stream
 *  stations are STORES — `seaCurrents` and `currents` in chart-layers — and a
 *  headless module in chart-core must not reach up into them. */
export interface PassageSources {
  /** The gates for a boat of this speed, or [] when none are known. */
  gates(boatKn: number): Gate[];
  /** The held model window, or null past its reach. `key` identifies it, so
   *  the worker knows when the window it is holding has been replaced. */
  field(): { geom: CurrentGeom; hours: PackedHour[]; key: string } | null;
  /** A sampler over that same window, for deriving the schedule on the main
   *  thread once the worker has answered. */
  sampler(): CurrentSampler;
}

function loadBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === 'true' || v === 'false') return v === 'true';
  } catch {
    /* default below */
  }
  return fallback;
}

function loadNum(key: string, fallback: number): number {
  try {
    const v = Number(localStorage.getItem(key));
    if (Number.isFinite(v) && v >= MIN_BOAT_KN && v <= MAX_BOAT_KN) return v;
  } catch {
    /* default below */
  }
  return fallback;
}

function load(): DepthSetting {
  try {
    const v = localStorage.getItem(SETTING_KEY);
    if (v === 'nearest' || v === 'near' || v === 'normal' || v === 'far') return v;
  } catch {
    /* default below */
  }
  return 'normal';
}

export const autoroute = new Autoroute();
